// Catalogue of industry-targeted SEO landing pages.
// Each entry powers a unique /<slug> route rendered through NicheLandingPage.
// Content is intentionally tailored per industry (not templated) so each page
// stands on its own for search engines.

const IMG = {
  office:
    "https://images.unsplash.com/photo-1703355685722-2996b01483be?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxwcml2YXRlJTIwb2ZmaWNlJTIwc3VpdGUlMjBtb2Rlcm4lMjB3aW5kb3d8ZW58MHx8fHwxNzgxMTkxMDc0fDA&ixlib=rb-4.1.0&q=85",
  meeting:
    "https://images.unsplash.com/photo-1628062699790-7c45262b82b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBvZmZpY2UlMjBtZWV0aW5nJTIwcm9vbXxlbnwwfHx8fDE3ODExOTEwNzR8MA&ixlib=rb-4.1.0&q=85",
  lobby:
    "https://images.pexels.com/photos/19893586/pexels-photo-19893586.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  lounge:
    "https://images.unsplash.com/photo-1758448511255-ac2a24a135d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwb2ZmaWNlJTIwbG91bmdlJTIwYXJlYXxlbnwwfHx8fDE3ODExOTEwNzR8MA&ixlib=rb-4.1.0&q=85",
  exterior:
    "https://images.unsplash.com/photo-1600531529272-023c4b821f14?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxvZmZpY2UlMjBidWlsZGluZyUyMGV4dGVyaW9yJTIwZ2xhc3MlMjBtb2Rlcm58ZW58MHx8fHwxNzgxMTkxMDgwfDA&ixlib=rb-4.1.0&q=85",
};

export const SEO_LANDINGS = [
  // ───────── LEGAL (7) ─────────
  {
    slug: "office-space-for-attorneys-weston-fl",
    category: "Legal",
    metaTitle: "Office Space for Attorneys in Weston, FL",
    metaDescription:
      "Private law office space for lease in Weston, FL — quiet, lockable suites with conference rooms, reception and 24/7 access. Schedule a tour at Weston Commercial Center.",
    h1: "Office space for attorneys in Weston, FL.",
    intro:
      "A quiet, lockable office, a reception team that greets your clients, and bookable boardrooms for depositions or mediations. Built for solo attorneys and boutique law firms in west Broward — ready to occupy within 72 hours.",
    audience: [
      "Solo attorneys & paralegals",
      "Boutique 2–8 attorney firms",
      "Of-counsel and contract litigators",
      "Mediators & arbitrators",
    ],
    benefits: [
      { title: "Privileged-call privacy", body: "Sound-treated phone booths and lockable offices for client calls under attorney-client privilege." },
      { title: "Bookable boardrooms", body: "Conference-room credits included monthly — book hourly for depositions, mediations, or client meetings." },
      { title: "Discreet reception", body: "Clients are greeted by name; process service and mail are handled with discretion." },
      { title: "Near Broward courthouse", body: "Minutes from the Plantation legal corridor and a direct I-75 shot to downtown Fort Lauderdale court complex." },
      { title: "Secure file storage", body: "Lockable cabinets, alarmed entry, and an on-site shredder for confidential disposal." },
      { title: "Transparent leases", body: "Plain-English month-to-month and 12-month terms — no CAM charges or pass-through fees." },
    ],
    recommendedSlugs: ["private-offices", "executive-suites", "office-suites"],
    heroImage: IMG.meeting,
    defaultOfficeType: "Private Offices",
  },
  {
    slug: "office-space-for-estate-planning-attorneys-weston-fl",
    category: "Legal",
    metaTitle: "Office Space for Estate Planning Attorneys in Weston, FL",
    metaDescription:
      "Office space tailored to estate planning attorneys in Weston, FL — private suites with a refined client meeting area, on-site notary access and concierge reception.",
    h1: "Office space for estate planning attorneys.",
    intro:
      "An office that looks the part for the conversations you have — wills, trusts, family-business transitions. Refined suites with proper client seating, signing tables, and an environment that puts older clients at ease.",
    audience: [
      "Solo estate planning attorneys",
      "Trusts & estates boutique firms",
      "Probate litigation practices",
      "Elder-law specialists",
    ],
    benefits: [
      { title: "Refined client meeting space", body: "Window-line suites with proper seating, signing tables, and the kind of calm older clients expect." },
      { title: "On-site notary access", body: "Notary services available in the building for document signings without coordinating a third party." },
      { title: "Witness-friendly conference rooms", body: "Boardrooms large enough to seat a family plus two witnesses and a signing agent comfortably." },
      { title: "Wheelchair-friendly access", body: "Step-free entry, accessible restrooms and elevator access on every floor." },
      { title: "Secure file rooms", body: "Lockable suites with alarmed building access for original wills, trusts, and titled documents." },
      { title: "Flexible signing-day parking", body: "Reserved visitor spaces at the front door for clients arriving for a multi-hour signing." },
    ],
    recommendedSlugs: ["executive-suites", "private-offices", "office-suites"],
    heroImage: IMG.office,
    defaultOfficeType: "Executive Suites",
  },
  {
    slug: "office-space-for-immigration-attorneys-weston-fl",
    category: "Legal",
    metaTitle: "Office Space for Immigration Attorneys in Weston, FL",
    metaDescription:
      "Immigration law office space for lease in Weston, FL — private consultation rooms, multilingual reception, and a welcoming environment for clients and their families.",
    h1: "Office space for immigration attorneys.",
    intro:
      "A practice that welcomes families — including kids, grandparents and translators — and an office that's ready for it. Private consultation rooms, a comfortable waiting area, and a reception team prepared for multi-generational visits.",
    audience: [
      "Solo immigration attorneys",
      "Family-based immigration practices",
      "Business immigration boutiques",
      "Asylum & humanitarian-law firms",
    ],
    benefits: [
      { title: "Family-friendly waiting area", body: "A clean, welcoming lobby with seating for entire families — not a row of chairs." },
      { title: "Private consultation rooms", body: "Multiple lockable rooms for sensitive interviews, away from open foot traffic." },
      { title: "Multilingual reception", body: "Front desk staff comfortable greeting Spanish-, Portuguese- and Creole-speaking clients." },
      { title: "Document scanning & copying", body: "On-site scanning, certified copying, and secure shredding included." },
      { title: "Walkable to translation services", body: "Three translation and notarisation services within a 5-minute walk for client referrals." },
      { title: "Free, reserved parking", body: "Covered parking for your team plus easy front-of-building parking for client arrivals." },
    ],
    recommendedSlugs: ["private-offices", "office-suites", "executive-suites"],
    heroImage: IMG.lobby,
    defaultOfficeType: "Private Offices",
  },
  {
    slug: "office-space-for-family-law-attorneys-weston-fl",
    category: "Legal",
    metaTitle: "Office Space for Family Law Attorneys in Weston, FL",
    metaDescription:
      "Family law & divorce attorney office space in Weston, FL — private, confidential suites, soundproofed meeting rooms, and a calming environment for difficult conversations.",
    h1: "Office space for family law & divorce attorneys.",
    intro:
      "Family law clients arrive emotional. Our offices are designed to lower the temperature — soundproofed meeting rooms, separated waiting areas if needed, and an environment that doesn't add stress to an already hard day.",
    audience: [
      "Family law & divorce attorneys",
      "Custody and dependency lawyers",
      "Domestic violence advocates",
      "Adoption attorneys",
    ],
    benefits: [
      { title: "Soundproofed meeting rooms", body: "Difficult conversations stay private — sound-treated walls and doors throughout the floor." },
      { title: "Separate waiting flexibility", body: "Two distinct waiting areas if a case requires keeping opposing parties apart pre-meeting." },
      { title: "Discreet reception", body: "Clients are checked in privately, not in a public-facing lobby with their full name announced." },
      { title: "Mediation-ready boardrooms", body: "Large enough for two parties, two attorneys and a mediator — with breakout space if needed." },
      { title: "Confidential file handling", body: "Locked offices, sealed shred bins, and never-shared visitor logs." },
      { title: "Flexible after-hours access", body: "24/7 keycard so you can prepare for trial nights and weekends." },
    ],
    recommendedSlugs: ["private-offices", "executive-suites", "office-suites"],
    heroImage: IMG.meeting,
    defaultOfficeType: "Private Offices",
  },
  {
    slug: "office-space-for-personal-injury-attorneys-weston-fl",
    category: "Legal",
    metaTitle: "Office Space for Personal Injury Attorneys in Weston, FL",
    metaDescription:
      "Personal injury law office space for lease in Weston, FL — accessible suites, a professional client meeting environment, and a strategic location minutes from I-75.",
    h1: "Office space for personal injury attorneys.",
    intro:
      "PI clients often arrive with mobility limitations, in active recovery, or with family in tow. We've built around that — wheelchair-accessible entries, generous parking, and conference rooms that don't feel clinical.",
    audience: [
      "Personal injury solo practitioners",
      "Plaintiff-side litigation boutiques",
      "Auto accident & negligence firms",
      "Workers' compensation attorneys",
    ],
    benefits: [
      { title: "Accessible from the curb", body: "Step-free entry, valet-style drop-off, elevators on every floor, and accessible restrooms." },
      { title: "Front-door parking", body: "Reserved visitor spaces immediately at the building entrance for clients in recovery." },
      { title: "Mediation-ready conference rooms", body: "Large boardrooms for settlement mediations, insurance adjusters, and IMEs." },
      { title: "Direct I-75 access", body: "Two minutes from the freeway — easy for clients commuting from Davie, Sunrise, and Pembroke Pines." },
      { title: "Branded signage opportunity", body: "Build-out options for branded reception walls — the kind of presence PI clients expect." },
      { title: "Scale up easily", body: "Start with a private office; expand to a multi-room suite as the docket grows." },
    ],
    recommendedSlugs: ["executive-suites", "office-suites", "private-offices"],
    heroImage: IMG.exterior,
    defaultOfficeType: "Executive Suites",
  },
  {
    slug: "office-space-for-business-attorneys-weston-fl",
    category: "Legal",
    metaTitle: "Office Space for Business & Corporate Attorneys in Weston, FL",
    metaDescription:
      "Corporate law office space in Weston, FL — executive suites, M&A-ready boardrooms, secure document storage and an established business address.",
    h1: "Office space for business & corporate attorneys.",
    intro:
      "Corporate clients judge the room. Our executive suites and large boardrooms project the substance your transactional and M&A work demands — and the building itself confirms it.",
    audience: [
      "Corporate & transactional attorneys",
      "M&A boutiques",
      "Commercial litigation practices",
      "General counsel-as-a-service firms",
    ],
    benefits: [
      { title: "M&A-ready boardrooms", body: "12-seat conference rooms with video, plus breakout offices for both sides during deal sprints." },
      { title: "Executive corner suites", body: "Window-line offices with the proportions and finish that match your client base." },
      { title: "Secure document handling", body: "Locked file rooms, alarmed building, and certified shredding for closing books and exhibits." },
      { title: "Established business address", body: "A serviced commercial building, not a coworking space — every detail signals stability." },
      { title: "Video-ready meeting rooms", body: "Pre-installed conferencing in every boardroom for remote counsel and clients." },
      { title: "Direct freeway access", body: "Two minutes to I-75, ideal for client visits from Miami, Boca, and Fort Lauderdale." },
    ],
    recommendedSlugs: ["executive-suites", "office-suites", "private-offices"],
    heroImage: IMG.meeting,
    defaultOfficeType: "Executive Suites",
  },
  {
    slug: "office-space-for-mediators-arbitrators-weston-fl",
    category: "Legal",
    metaTitle: "Office Space for Mediators & Arbitrators in Weston, FL",
    metaDescription:
      "Office space for mediators, arbitrators and ADR professionals in Weston, FL — multiple breakout rooms, neutral meeting space, and a discreet, professional environment.",
    h1: "Office space for mediators & arbitrators.",
    intro:
      "Mediations need three rooms, not one — a joint room and two caucus rooms, all on the same floor. We've configured space that makes shuttle diplomacy easy and keeps parties separated.",
    audience: [
      "Solo mediators",
      "Arbitrators & ADR panels",
      "Family-law mediators",
      "Commercial dispute resolution professionals",
    ],
    benefits: [
      { title: "Three-room mediation setup", body: "Joint room plus two private caucus rooms on the same floor — no awkward elevator runs." },
      { title: "Neutral, calm interiors", body: "Designed to read as a third-party space, not anyone's home turf." },
      { title: "Hourly room rentals", body: "Need just three rooms for a day? Book them by the hour without taking a full lease." },
      { title: "Discreet reception", body: "Two arrival paths if parties need to be checked in separately." },
      { title: "Video-conferencing ready", body: "All conference rooms pre-installed with HD video for hybrid mediations." },
      { title: "Tenant of record privacy", body: "Your name on the suite — not splashed on a lobby directory if you prefer." },
    ],
    recommendedSlugs: ["office-suites", "executive-suites", "private-offices"],
    heroImage: IMG.meeting,
    defaultOfficeType: "Office Suites",
  },

  // ───────── FINANCIAL (6) ─────────
  {
    slug: "office-space-for-accountants-cpas-weston-fl",
    category: "Financial Services",
    metaTitle: "Office Space for Accountants & CPAs in Weston, FL",
    metaDescription:
      "CPA and accounting firm office space for lease in Weston, FL — secure suites, conference rooms for client reviews, and flexible terms that scale through tax season.",
    h1: "Office space for accountants & CPAs.",
    intro:
      "From January to April, you need every desk filled. From May to December, half that's plenty. Our flexible suites scale with your seasonality — and the secure environment fits the data you handle year-round.",
    audience: [
      "Solo CPAs & tax preparers",
      "Boutique accounting firms (2–8 staff)",
      "Outsourced CFO practices",
      "Audit & advisory boutiques",
    ],
    benefits: [
      { title: "Tax-season scalability", body: "Add desks for the busy season and shed them after April — without breaking your lease." },
      { title: "Secure document handling", body: "Locked offices, alarmed entry, and certified on-site shredding for client tax docs." },
      { title: "Private client review rooms", body: "Conference rooms perfect for sitting down with clients to walk through returns." },
      { title: "Gigabit business internet", body: "Redundant fibre — because nothing kills productivity like a portal that won't load on April 14." },
      { title: "Mail handling for tax filings", body: "Reception signs for certified mail, IRS correspondence, and overnight tax packages." },
      { title: "After-hours building access", body: "24/7 keycard access for the late nights and weekends you'll actually work." },
    ],
    recommendedSlugs: ["private-offices", "office-suites", "executive-suites"],
    heroImage: IMG.office,
    defaultOfficeType: "Private Offices",
  },
  {
    slug: "office-space-for-tax-preparers-weston-fl",
    category: "Financial Services",
    metaTitle: "Office Space for Tax Preparers in Weston, FL",
    metaDescription:
      "Tax preparer office space in Weston, FL — affordable private suites, walk-in friendly reception, and seasonal flexibility for tax-prep practices.",
    h1: "Office space for tax preparers.",
    intro:
      "A serviced building that takes walk-ins seriously. Visible signage, a real receptionist, and an environment that makes clients feel like they're with a professional — not in someone's home office.",
    audience: [
      "Solo tax preparers",
      "Seasonal tax-prep practices",
      "EAs (Enrolled Agents)",
      "Bilingual tax services",
    ],
    benefits: [
      { title: "Walk-in friendly reception", body: "Front desk welcomes clients arriving without an appointment — common in tax-prep months." },
      { title: "Visible building signage", body: "Lobby directory listing and optional suite-door signage that builds neighborhood recognition." },
      { title: "Seasonal lease terms", body: "Short-term and seasonal options for practices that scale up January through April only." },
      { title: "Affordable starting rates", body: "Private offices from $895/month — fully furnished, internet included." },
      { title: "Bilingual reception support", body: "Spanish-speaking reception staff to welcome clients comfortably." },
      { title: "Cash & card-friendly", body: "Secure on-site space for clients paying same-day; no fumbling with cash in a coworking lobby." },
    ],
    recommendedSlugs: ["private-offices", "executive-suites", "office-suites"],
    heroImage: IMG.office,
    defaultOfficeType: "Private Offices",
  },
  {
    slug: "office-space-for-financial-advisors-weston-fl",
    category: "Financial Services",
    metaTitle: "Office Space for Financial Advisors & Wealth Managers in Weston, FL",
    metaDescription:
      "Executive office space for financial advisors and wealth management firms in Weston, FL — refined suites, quiet client environments, and a serviced building that builds client confidence.",
    h1: "Office space for financial advisors & wealth managers.",
    intro:
      "Your clients trust you with everything. The space they meet you in should confirm that trust — refined, quiet, and serviced. Our executive suites are built for the conversations that grow assets under management.",
    audience: [
      "Independent financial advisors (RIAs)",
      "Wealth management boutiques",
      "Insurance & annuity specialists",
      "Retirement planning practices",
    ],
    benefits: [
      { title: "Refined client environment", body: "Window-line executive suites with designer furniture and the calm finishes UHNW clients expect." },
      { title: "Compliance-ready privacy", body: "Lockable offices, private meeting rooms, and the SEC-grade discretion your records require." },
      { title: "On-site reception", body: "Clients greeted by name; no badge-printing kiosks or impersonal coworking check-ins." },
      { title: "Recorded-call space", body: "Sound-isolated phone booths for compliance-recorded client conversations." },
      { title: "Established business address", body: "A long-tenured commercial building, not a flexible-office brand that re-paints every two years." },
      { title: "Secure client document storage", body: "Locked file rooms with alarmed building access for original signed documents." },
    ],
    recommendedSlugs: ["executive-suites", "office-suites", "private-offices"],
    heroImage: IMG.office,
    defaultOfficeType: "Executive Suites",
  },
  {
    slug: "office-space-for-insurance-agencies-weston-fl",
    category: "Financial Services",
    metaTitle: "Office Space for Insurance Agencies in Weston, FL",
    metaDescription:
      "Insurance agency office space for lease in Weston, FL — multi-room suites for producers and account managers, walk-in friendly reception, covered parking and branded signage.",
    h1: "Office space for insurance agencies.",
    intro:
      "Insurance agencies need three things from an office: room for producers, somewhere clients can walk in with a policy question, and a building that looks the part. We have all three.",
    audience: [
      "P&C insurance agencies",
      "Life & health insurance brokers",
      "Commercial lines agencies",
      "Public adjuster firms",
    ],
    benefits: [
      { title: "Multi-producer suites", body: "Office suites with 3–6 private offices plus a shared support-staff bullpen." },
      { title: "Walk-in client friendly", body: "Front-of-house reception trained for the policy questions and claim walk-ins your business sees daily." },
      { title: "Branded signage", body: "Suite-door and lobby-directory signage options — build neighborhood recognition for your agency." },
      { title: "Direct-dial phone lines", body: "Dedicated business phone lines per producer, not shared coworking numbers." },
      { title: "Plenty of visitor parking", body: "Front-of-building visitor parking for the daily flow of policy-question drop-ins." },
      { title: "Storage for files & swag", body: "Lockable storage for client files, policy binders, and the inevitable promotional swag." },
    ],
    recommendedSlugs: ["office-suites", "team-offices", "executive-suites"],
    heroImage: IMG.lobby,
    defaultOfficeType: "Office Suites",
  },
  {
    slug: "office-space-for-mortgage-brokers-weston-fl",
    category: "Financial Services",
    metaTitle: "Office Space for Mortgage Brokers in Weston, FL",
    metaDescription:
      "Mortgage broker office space for lease in Weston, FL — private signing rooms, secure document handling, and a professional environment for borrowers and lenders alike.",
    h1: "Office space for mortgage brokers.",
    intro:
      "Borrowers sign their largest financial document of the year in your office. Make sure it looks like the moment deserves — and that the documents are handled with the security underwriters expect.",
    audience: [
      "Solo mortgage brokers",
      "Mortgage broker teams (3–10)",
      "Commercial real estate lenders",
      "Hard-money lenders",
    ],
    benefits: [
      { title: "Private signing rooms", body: "Closing-table-style rooms with proper seating, signing space, and a calm environment." },
      { title: "Secure document handling", body: "Locked offices and alarmed building — borrowers' financial docs handled to lender standards." },
      { title: "Co-broker meeting space", body: "Boardrooms for joint appointments with realtors, title agents, and lender reps." },
      { title: "Title-company adjacency", body: "Walking distance to two title companies for same-day closings and last-minute notarisations." },
      { title: "Branded suite signage", body: "Suite-door signage so referral partners know exactly where to send borrowers." },
      { title: "Flexible terms", body: "Month-to-month and 12-month leases as rate environments shift." },
    ],
    recommendedSlugs: ["private-offices", "executive-suites", "office-suites"],
    heroImage: IMG.office,
    defaultOfficeType: "Private Offices",
  },
  {
    slug: "office-space-for-bookkeepers-weston-fl",
    category: "Financial Services",
    metaTitle: "Office Space for Bookkeepers in Weston, FL",
    metaDescription:
      "Bookkeeping office space for lease in Weston, FL — affordable private offices, secure file handling, and flexible terms designed for solo and small-team bookkeepers.",
    h1: "Office space for bookkeepers.",
    intro:
      "Most of your day is heads-down reconciliation work. You need quiet, secure, and affordable — and a professional address when clients drop off shoeboxes of receipts.",
    audience: [
      "Solo bookkeepers",
      "QuickBooks ProAdvisor practices",
      "Outsourced accounting departments",
      "Payroll-services boutiques",
    ],
    benefits: [
      { title: "Quiet, focused offices", body: "Lockable private offices on quieter floors — built for reconciliation work, not for foot traffic." },
      { title: "Secure document drop-off", body: "Reception accepts and logs client document deliveries with a printed receipt." },
      { title: "Affordable private suites", body: "From $895/month furnished — competitive with coworking memberships but private." },
      { title: "Gigabit internet", body: "Redundant fibre so QuickBooks Online, Xero, and ADP never time out on close day." },
      { title: "Lockable file storage", body: "Locked cabinets and on-site shredding for client financial records." },
      { title: "Monthly terms", body: "Start month-to-month while you build a client base; convert to annual when ready." },
    ],
    recommendedSlugs: ["private-offices", "executive-suites", "office-suites"],
    heroImage: IMG.office,
    defaultOfficeType: "Private Offices",
  },

  // ───────── REAL ESTATE (3) ─────────
  {
    slug: "office-space-for-real-estate-brokers-weston-fl",
    category: "Real Estate",
    metaTitle: "Office Space for Real Estate Brokers in Weston, FL",
    metaDescription:
      "Real estate brokerage office space for lease in Weston, FL — multi-room suites for agents, branded reception, conference rooms for client signings and flexible terms.",
    h1: "Office space for real estate brokers.",
    intro:
      "Your agents are out showing — but the brokerage needs an address with weight. Multi-room office suites with a branded reception, conference rooms for closings, and the kind of building that wins the listing presentation.",
    audience: [
      "Independent real estate brokerages",
      "Boutique luxury agencies",
      "Commercial real estate teams",
      "Property investment groups",
    ],
    benefits: [
      { title: "Branded suite signage", body: "Lobby-directory and suite-door signage — your brand visible to every visitor in the building." },
      { title: "Multi-agent suites", body: "Suites configured for 4–10 agents plus support staff, with private offices for senior producers." },
      { title: "Closing-day boardrooms", body: "Conference rooms perfect for full-table closings with buyer, seller, attorneys, and title agents." },
      { title: "Visitor parking", body: "Plenty of front-of-building parking for buyers and sellers arriving for appointments." },
      { title: "Listing-presentation polish", body: "A serviced commercial address that wins the trust of high-end sellers before you've shown a single comp." },
      { title: "Flexible team growth", body: "Add agents and desks monthly without renegotiating a full lease." },
    ],
    recommendedSlugs: ["office-suites", "team-offices", "executive-suites"],
    heroImage: IMG.lobby,
    defaultOfficeType: "Office Suites",
  },
  {
    slug: "office-space-for-title-companies-weston-fl",
    category: "Real Estate",
    metaTitle: "Office Space for Title Companies in Weston, FL",
    metaDescription:
      "Title company office space for lease in Weston, FL — closing-room ready suites, secure document handling, and a strategic location for high-volume closings.",
    h1: "Office space for title companies.",
    intro:
      "Closings happen in the conference room. Ours are sized for the full table — buyer, seller, both attorneys, the lender rep, the realtors, and the closing agent — with breakout space when the deal stalls.",
    audience: [
      "Title agencies",
      "Settlement services companies",
      "Escrow companies",
      "1031 exchange intermediaries",
    ],
    benefits: [
      { title: "Closing-day boardrooms", body: "Conference rooms sized for the full closing table plus breakout offices for renegotiation." },
      { title: "Secure document handling", body: "Locked suites, alarmed building, and certified shredding for closing books and originals." },
      { title: "Realtor & broker proximity", body: "Surrounded by real estate agencies and mortgage brokers — built-in referral network." },
      { title: "Reception for couriers", body: "Receptionists trained for the daily flow of FedEx, UPS, courier-delivered originals, and same-day signings." },
      { title: "Direct freeway access", body: "Out-of-area buyers and sellers reach you in minutes from I-75 or the Sawgrass." },
      { title: "Walk-in friendly", body: "Drop-in notarisations, document drops, and last-minute signature gathering handled at the front desk." },
    ],
    recommendedSlugs: ["office-suites", "executive-suites", "private-offices"],
    heroImage: IMG.lobby,
    defaultOfficeType: "Office Suites",
  },
  {
    slug: "office-space-for-property-management-companies-weston-fl",
    category: "Real Estate",
    metaTitle: "Office Space for Property Management Companies in Weston, FL",
    metaDescription:
      "Property management office space for lease in Weston, FL — multi-room suites, owner & tenant-friendly reception, and a central location for portfolios across Broward.",
    h1: "Office space for property management companies.",
    intro:
      "Property managers field tenant calls, owner check-ins, vendor coordination, and the occasional emergency — all at once. We've built space that handles all four without anyone hearing each other.",
    audience: [
      "Residential property managers",
      "Commercial property managers",
      "HOA management firms",
      "Short-term rental management",
    ],
    benefits: [
      { title: "Multi-staff suites", body: "Office suites configured for office managers, accountants, and on-call coordinators in a single footprint." },
      { title: "Tenant & owner-friendly reception", body: "Front desk fields rent drop-offs, key handoffs, and owner check-ins gracefully." },
      { title: "Sound-treated offices", body: "Difficult tenant calls stay private; angry owners don't hear other angry owners." },
      { title: "Vendor coordination space", body: "Boardrooms for contractor walk-throughs, RFP reviews, and capital-project planning." },
      { title: "Storage for keys & supplies", body: "Lockable storage for keys, mailbox keys, lockboxes, and small maintenance supplies." },
      { title: "Central Broward location", body: "Drive to portfolios in Weston, Sunrise, Plantation, Davie, and Pembroke Pines from one base." },
    ],
    recommendedSlugs: ["office-suites", "team-offices", "executive-suites"],
    heroImage: IMG.exterior,
    defaultOfficeType: "Office Suites",
  },

  // ───────── MARKETING / PR (3) ─────────
  {
    slug: "office-space-for-marketing-agencies-weston-fl",
    category: "Marketing & Creative",
    metaTitle: "Office Space for Marketing Agencies in Weston, FL",
    metaDescription:
      "Marketing agency office space for lease in Weston, FL — open-plan team offices, client-pitch boardrooms, and a building that lets you scale without renegotiating leases.",
    h1: "Office space for marketing agencies.",
    intro:
      "Agencies grow in fits and starts — six people one quarter, eleven the next. We've sized our team offices for exactly that ramp, with the pitch-room finishes that win the brief in the first place.",
    audience: [
      "Full-service marketing agencies",
      "Brand & creative studios",
      "Performance marketing teams",
      "B2B agencies & lead-gen shops",
    ],
    benefits: [
      { title: "Open-plan team offices", body: "Configurable team space for 6–25 with strategists, designers and account managers in one room." },
      { title: "Client-pitch boardrooms", body: "Wide screens, hard-wired video, and presentation-ready lighting — your deck looks the part." },
      { title: "Quiet phone booths", body: "Account managers can take confidential calls without breaking the open-plan energy." },
      { title: "Bike storage & showers", body: "A small thing that hires creative talent — they want to bike to work." },
      { title: "Scale month to month", body: "Add a desk, drop a desk, swap to a bigger suite — without redoing your lease." },
      { title: "Print-ready package handling", body: "Reception receives print samples, brand merch, and event materials all day." },
    ],
    recommendedSlugs: ["team-offices", "office-suites", "executive-suites"],
    heroImage: IMG.lounge,
    defaultOfficeType: "Team Offices",
  },
  {
    slug: "office-space-for-digital-marketing-seo-agencies-weston-fl",
    category: "Marketing & Creative",
    metaTitle: "Office Space for Digital Marketing & SEO Agencies in Weston, FL",
    metaDescription:
      "Digital marketing and SEO agency office space in Weston, FL — gigabit internet, open-plan team layouts, podcast-ready booths and flexible terms.",
    h1: "Office space for digital marketing & SEO agencies.",
    intro:
      "Your team lives in Search Console, Looker Studio, and Slack DMs. The office just needs to disappear behind the work — fast internet, quiet pods for calls, and enough room when a client visits in person.",
    audience: [
      "SEO & content agencies",
      "Paid media (PPC) shops",
      "Email & lifecycle marketing teams",
      "Webflow & WordPress dev studios",
    ],
    benefits: [
      { title: "Gigabit redundant internet", body: "Two carriers, automatic failover — because nothing kills productivity like a Looker load timeout." },
      { title: "Podcast & video-ready booths", body: "Sound-treated booths for client podcasts, sales videos, and async Loom recordings." },
      { title: "Open layouts that scale", body: "Configure 6 to 25 desks — and rearrange without negotiating with a landlord." },
      { title: "Client visit boardrooms", body: "Quarterly business reviews in a room that looks the part, not your local Starbucks." },
      { title: "Coffee that earns its keep", body: "Specialty coffee bar in the tenant lounge — agency morale's not-so-secret weapon." },
      { title: "Hybrid-friendly terms", body: "Most of the team works remotely most days — our flexible leases match how your team actually shows up." },
    ],
    recommendedSlugs: ["team-offices", "office-suites", "executive-suites"],
    heroImage: IMG.lounge,
    defaultOfficeType: "Team Offices",
  },
  {
    slug: "office-space-for-public-relations-firms-weston-fl",
    category: "Marketing & Creative",
    metaTitle: "Office Space for Public Relations Firms in Weston, FL",
    metaDescription:
      "PR & communications agency office space for lease in Weston, FL — pitch-ready boardrooms, broadcast-friendly booths, and an address that suits the firms you represent.",
    h1: "Office space for public relations firms.",
    intro:
      "PR runs on relationships and reputation. Both depend on the building you take a journalist or a CMO to for the meeting that closes the engagement. Ours is built for both.",
    audience: [
      "PR & communications agencies",
      "Public affairs consultancies",
      "Crisis management firms",
      "Influencer & talent management",
    ],
    benefits: [
      { title: "Pitch-perfect boardrooms", body: "Conference rooms designed for client-pitch presentations and journalist interviews alike." },
      { title: "Broadcast-friendly booths", body: "Sound-treated rooms ideal for live radio hits, podcast interviews and on-camera commentary." },
      { title: "Reception that knows the room", body: "Front desk used to handling visiting executives, reporters, and the occasional camera crew." },
      { title: "Visitor parking on call", body: "Reserved guest spaces for time-sensitive press visits and crisis-day visiting executives." },
      { title: "Address that punches above its weight", body: "A serviced commercial building — not an apartment-conversion or coworking centre." },
      { title: "Flexible team configurations", body: "Scale up before a major launch; right-size after — without contract negotiations." },
    ],
    recommendedSlugs: ["executive-suites", "office-suites", "team-offices"],
    heroImage: IMG.meeting,
    defaultOfficeType: "Executive Suites",
  },

  // ───────── CONSULTING / HR (4) ─────────
  {
    slug: "office-space-for-management-consultants-weston-fl",
    category: "Consulting",
    metaTitle: "Office Space for Management Consultants in Weston, FL",
    metaDescription:
      "Management consulting office space for lease in Weston, FL — executive suites, video-ready boardrooms, and a serviced address that matches the engagements you sell.",
    h1: "Office space for management consultants.",
    intro:
      "Consultants charge by the day. The building should never be the reason a prospect questions the rate. Executive suites and a serviced reception keep the focus on the work — and the close.",
    audience: [
      "Independent management consultants",
      "Strategy & operations boutiques",
      "Change management practices",
      "Fractional executive firms",
    ],
    benefits: [
      { title: "Executive-grade suites", body: "Window-line offices and designer finishes — the room confirms the rate." },
      { title: "Video-ready boardrooms", body: "Hybrid-friendly rooms for client workshops with remote partners and on-site stakeholders." },
      { title: "Concierge reception", body: "Visiting C-suite executives are greeted by name and offered coffee — every time." },
      { title: "Polished print & scan", body: "Branded deliverables printed and handed to clients without anyone seeing the kitchen-table operation." },
      { title: "Flexible by the week", body: "Take a suite when you're delivering on-site, release it when you're at a client — month-to-month options." },
      { title: "Established commercial address", body: "A serviced building, not a coworking lobby — the right answer when clients ask where to send the contract." },
    ],
    recommendedSlugs: ["executive-suites", "private-offices", "office-suites"],
    heroImage: IMG.office,
    defaultOfficeType: "Executive Suites",
  },
  {
    slug: "office-space-for-business-coaches-weston-fl",
    category: "Consulting",
    metaTitle: "Office Space for Business & Executive Coaches in Weston, FL",
    metaDescription:
      "Office space for business and executive coaches in Weston, FL — calm, private suites for one-on-one sessions, group workshop space, and flexible terms.",
    h1: "Office space for business & executive coaches.",
    intro:
      "Coaching happens in a room. The room should help — calm, private, well-lit, and away from your own home or your client's distractions. We've built that room.",
    audience: [
      "Executive coaches & ICF practitioners",
      "Business strategists",
      "Sales & leadership coaches",
      "Career & transition coaches",
    ],
    benefits: [
      { title: "Calm one-on-one suites", body: "Private offices designed for 1:1 sessions — quiet, well-lit, comfortable seating." },
      { title: "Workshop-ready rooms", body: "Bookable larger rooms for cohort programs, leadership offsites, and group intensives." },
      { title: "Confidential, sound-treated", body: "Conversations stay in the room — sound-treated walls and doors throughout." },
      { title: "Discreet check-in", body: "Clients arrive privately; no public-facing lobby badge or announcement." },
      { title: "Coffee, tea, water — handled", body: "Hospitality details that make a session feel like a session, not an office visit." },
      { title: "Hour, day, month rentals", body: "Take a suite full-time, or rent a private room by the half-day for monthly cohort meetings." },
    ],
    recommendedSlugs: ["private-offices", "executive-suites", "office-suites"],
    heroImage: IMG.office,
    defaultOfficeType: "Private Offices",
  },
  {
    slug: "office-space-for-hr-consultants-staffing-agencies-weston-fl",
    category: "Consulting",
    metaTitle: "Office Space for HR Consultants & Staffing Agencies in Weston, FL",
    metaDescription:
      "HR consulting and staffing agency office space for lease in Weston, FL — private interview rooms, multi-staff suites, and a candidate-friendly reception.",
    h1: "Office space for HR consultants & staffing agencies.",
    intro:
      "Interviews happen all day. Candidates arrive nervous; clients arrive curious. Our suites have the private rooms for the interviews and the polished reception that sets the right tone the moment someone walks in.",
    audience: [
      "Staffing & recruiting agencies",
      "HR consulting practices",
      "Executive search firms",
      "PEO & payroll consultancies",
    ],
    benefits: [
      { title: "Multiple interview rooms", body: "Run three interviews in parallel — each in a private, sound-treated room." },
      { title: "Candidate-friendly reception", body: "Front desk that puts nervous candidates at ease the moment they arrive." },
      { title: "Multi-staff suites", body: "Office suites for 4–10 recruiters with a shared client-services bullpen." },
      { title: "Background-check ready space", body: "Lockable rooms for ID verification, document scanning, and W-4 / I-9 handling." },
      { title: "Direct freeway access", body: "Candidates from across Broward and Miami reach you in one easy drive." },
      { title: "Flexible to busy seasons", body: "Scale up for a hiring sprint; release space when the role's filled." },
    ],
    recommendedSlugs: ["office-suites", "team-offices", "private-offices"],
    heroImage: IMG.lobby,
    defaultOfficeType: "Office Suites",
  },
  {
    slug: "office-space-for-executive-recruiters-weston-fl",
    category: "Consulting",
    metaTitle: "Office Space for Executive Recruiters in Weston, FL",
    metaDescription:
      "Executive recruiter office space in Weston, FL — discreet candidate meeting rooms, executive-grade suites, and the privacy retained search demands.",
    h1: "Office space for executive recruiters.",
    intro:
      "Retained search is built on discretion. Candidates aren't comfortable in coworking lobbies and clients won't sign engagements unless the brand has weight. We solve both.",
    audience: [
      "Retained executive search firms",
      "C-suite recruiting boutiques",
      "Board search practices",
      "Specialised functional search",
    ],
    benefits: [
      { title: "Discreet candidate meetings", body: "Private suites with separate entry paths — candidates and clients don't bump into each other." },
      { title: "Executive-grade environment", body: "The building reads as established and credible — the moment a CEO walks in." },
      { title: "Reception trained for VIPs", body: "Front desk handles visiting executives, board members, and confidential drop-ins with care." },
      { title: "Sound-treated for sensitive calls", body: "Confidential reference checks and offer negotiations stay between the parties involved." },
      { title: "Private parking access", body: "Reserved spaces away from foot traffic for high-profile candidate visits." },
      { title: "Address that holds up", body: "A serviced commercial address that doesn't undermine your $40K-engagement positioning." },
    ],
    recommendedSlugs: ["executive-suites", "private-offices", "office-suites"],
    heroImage: IMG.office,
    defaultOfficeType: "Executive Suites",
  },

  // ───────── ARCH / ENG / DESIGN (3) ─────────
  {
    slug: "office-space-for-architects-weston-fl",
    category: "Architecture & Engineering",
    metaTitle: "Office Space for Architects in Weston, FL",
    metaDescription:
      "Architectural firm office space in Weston, FL — natural light, drafting-ready desks, client-presentation rooms and a serviced building that suits the practice.",
    h1: "Office space for architects.",
    intro:
      "Architects need light, wall space for the work, and a room that looks the part when the client walks through the door. Window-line suites and presentation-ready boardrooms make this easy.",
    audience: [
      "Boutique architecture firms",
      "Solo architects & designers",
      "Residential architecture practices",
      "Commercial & retail architects",
    ],
    benefits: [
      { title: "Natural-light suites", body: "Window-line offices — drafting and renderings deserve real daylight, not corporate fluorescents." },
      { title: "Wall space for pinups", body: "Big walls for plan reviews, mood boards, and client design conversations." },
      { title: "Presentation-ready boardrooms", body: "Wide screens, dimmable lighting, and the calm needed to talk through a building." },
      { title: "Large-format print friendly", body: "Couriers and large-format deliveries handled at reception — no oversized envelope drama." },
      { title: "Plotter & sample storage", body: "Lockable storage for materials samples, plotter rolls, and physical models in progress." },
      { title: "Hybrid-friendly leases", body: "Most architects split between site visits and the office — flexible leases that match." },
    ],
    recommendedSlugs: ["executive-suites", "office-suites", "team-offices"],
    heroImage: IMG.office,
    defaultOfficeType: "Executive Suites",
  },
  {
    slug: "office-space-for-engineering-firms-weston-fl",
    category: "Architecture & Engineering",
    metaTitle: "Office Space for Engineering Firms in Weston, FL",
    metaDescription:
      "Engineering firm office space for lease in Weston, FL — team suites, project-room boardrooms, secure document handling and a strategic Broward location.",
    h1: "Office space for engineering firms.",
    intro:
      "Engineering projects run on rooms — project rooms, client review rooms, quiet rooms for calc-heavy work. We've built the space mix that makes practice management easier.",
    audience: [
      "Civil engineering firms",
      "Structural engineering practices",
      "MEP consultants",
      "Environmental & geotechnical engineers",
    ],
    benefits: [
      { title: "Project-room flexibility", body: "Larger conference rooms that can be standing project rooms for the life of a delivery cycle." },
      { title: "Multi-discipline team suites", body: "Office suites configured for civil, structural and MEP teams working in parallel." },
      { title: "Document & drawing handling", body: "Reception receives, logs and stores incoming permits, RFIs and stamped drawings." },
      { title: "Secure storage", body: "Locked file rooms and alarmed building for sealed plans and confidential client records." },
      { title: "Direct I-75 access", body: "Reach project sites across Broward, Palm Beach and Miami-Dade in a single drive." },
      { title: "Bookable boardrooms", body: "Client-review and code-review rooms by the hour — book when you need, release when you don't." },
    ],
    recommendedSlugs: ["office-suites", "team-offices", "executive-suites"],
    heroImage: IMG.meeting,
    defaultOfficeType: "Office Suites",
  },
  {
    slug: "office-space-for-interior-designers-weston-fl",
    category: "Architecture & Engineering",
    metaTitle: "Office Space for Interior Designers in Weston, FL",
    metaDescription:
      "Interior design studio office space for lease in Weston, FL — light-filled suites for sample selection, client-presentation rooms and storage for materials libraries.",
    h1: "Office space for interior designers.",
    intro:
      "Designers select materials in real light, present mood boards to clients, and need somewhere to store the inevitable sample libraries. Our window-line suites and tenant storage make this practical.",
    audience: [
      "Residential interior designers",
      "Commercial / hospitality designers",
      "Stagers & redesign professionals",
      "Showroom-light boutique studios",
    ],
    benefits: [
      { title: "Window-line suites", body: "Real daylight for evaluating fabrics, finishes and paint chips — not under fluorescent lights." },
      { title: "Wall space for boards", body: "Plenty of wall to lay out mood boards, sample boards, and project schedules." },
      { title: "Materials storage", body: "Lockable on-floor storage for sample libraries, fabric books and stocked product." },
      { title: "Client-presentation rooms", body: "Bookable, well-lit rooms ideal for client mood-board reviews and final approvals." },
      { title: "Delivery-friendly reception", body: "Front desk handles oversized sample deliveries, vendor drop-offs, and trade show pickups." },
      { title: "Hour-based room rentals", body: "Rent a presentation room by the hour for client meetings when you don't need a full-time suite." },
    ],
    recommendedSlugs: ["private-offices", "executive-suites", "office-suites"],
    heroImage: IMG.lounge,
    defaultOfficeType: "Private Offices",
  },

  // ───────── TECHNOLOGY (3) ─────────
  {
    slug: "office-space-for-it-consultants-msps-weston-fl",
    category: "Technology",
    metaTitle: "Office Space for IT Consultants & MSPs in Weston, FL",
    metaDescription:
      "IT consultant and managed services provider office space in Weston, FL — secure suites, redundant internet, helpdesk-ready open space and 24/7 access.",
    h1: "Office space for IT consultants & MSPs.",
    intro:
      "Tickets come in around the clock — sometimes from clients in the building. Our 24/7 access, gigabit fibre and helpdesk-friendly suites make the practice run.",
    audience: [
      "Managed services providers (MSPs)",
      "IT consulting firms",
      "Cybersecurity consultants",
      "Network & infrastructure specialists",
    ],
    benefits: [
      { title: "Redundant gigabit internet", body: "Two-carrier fibre with automatic failover — clients calling about their outage shouldn't reach yours." },
      { title: "Locked IT closet space", body: "Suites configured with secure storage for spare equipment, replacement hardware, and racks." },
      { title: "24/7 access", body: "Walk in at 2 AM to triage an incident — keycard access any day of the year." },
      { title: "Helpdesk-ready open layouts", body: "Configurable team space for tier-1 support, plus quiet rooms for escalations." },
      { title: "Client-base inside the building", body: "120+ tenants in the building — start with prospects on your floor before you cold-call town." },
      { title: "Conference-room demos", body: "Video-ready boardrooms for prospect demos and quarterly business reviews." },
    ],
    recommendedSlugs: ["team-offices", "office-suites", "private-offices"],
    heroImage: IMG.lounge,
    defaultOfficeType: "Team Offices",
  },
  {
    slug: "office-space-for-tech-startups-weston-fl",
    category: "Technology",
    metaTitle: "Office Space for Tech Startups in Weston, FL",
    metaDescription:
      "Office space for tech startups and software companies in Weston, FL — flexible team layouts, monthly leases, gigabit internet and room to scale from 4 to 25.",
    h1: "Office space for tech startups & software companies.",
    intro:
      "Startups need to commit to a city, not a 5-year lease. Take a team office for the seed-stage four people; scale it as you raise; release it if the round doesn't close. Florida-friendly tax base, year-round talent.",
    audience: [
      "Early-stage SaaS startups",
      "Bootstrapped product teams",
      "Fintech, proptech & vertical SaaS",
      "Remote-first teams returning to office",
    ],
    benefits: [
      { title: "Scale 4 → 25 without moving", body: "Move between suite sizes as headcount grows — same building, same lease cycle, no broker fees." },
      { title: "Gigabit redundant internet", body: "Production deploys and remote pair-programming need uptime — we deliver." },
      { title: "Monthly terms while you raise", body: "Month-to-month options while you're closing the round; convert to 12-month after." },
      { title: "Investor-meeting boardrooms", body: "Video-ready rooms when an investor flies down for the deck review." },
      { title: "Florida tax advantages", body: "Build the company in a no-state-income-tax jurisdiction — and an established commercial address that signals seriousness." },
      { title: "Hybrid-friendly", body: "Most startup teams hybrid by default; our flexible desk count matches how teams really show up." },
    ],
    recommendedSlugs: ["team-offices", "office-suites", "private-offices"],
    heroImage: IMG.lounge,
    defaultOfficeType: "Team Offices",
  },
  {
    slug: "office-space-for-cybersecurity-firms-weston-fl",
    category: "Technology",
    metaTitle: "Office Space for Cybersecurity Firms in Weston, FL",
    metaDescription:
      "Cybersecurity firm office space in Weston, FL — secure suites, locked offices, redundant internet and a hardened building for the practice that demands it.",
    h1: "Office space for cybersecurity firms.",
    intro:
      "If you're advising clients on physical and digital security, your own office is the proof of concept. We've built the kind of hardened, monitored environment your clients expect from you.",
    audience: [
      "Cybersecurity consultancies",
      "Pen-test & red-team boutiques",
      "Compliance & audit firms (SOC 2, HIPAA, PCI)",
      "SOC-as-a-service providers",
    ],
    benefits: [
      { title: "Hardened building access", body: "Keycard access at the lobby and on every floor; cameras on every common area." },
      { title: "Locked, monitored suites", body: "Every suite is individually lockable; alarmed building with daytime security." },
      { title: "Redundant internet", body: "Two-carrier fibre — your SOC dashboards stay up even if one provider doesn't." },
      { title: "Secure shredding & disposal", body: "Certified on-site shredding for the engagement reports you don't want surfacing." },
      { title: "Private boardrooms", body: "Locked, sound-treated rooms for tabletop exercises and breach-response simulations." },
      { title: "Established business address", body: "Clients vetting their security partners want to see a credible business — not a coworking lobby." },
    ],
    recommendedSlugs: ["private-offices", "office-suites", "executive-suites"],
    heroImage: IMG.exterior,
    defaultOfficeType: "Private Offices",
  },

  // ───────── OTHER PROFESSIONAL (4) ─────────
  {
    slug: "office-space-for-notary-services-weston-fl",
    category: "Other Professional Services",
    metaTitle: "Office Space for Notary & Document Services in Weston, FL",
    metaDescription:
      "Notary and document services office space in Weston, FL — walk-in friendly reception, private signing rooms and flexible hourly room rentals.",
    h1: "Office space for notary & document services.",
    intro:
      "Most notarisations take 15 minutes. Most signings need a quiet room and a witness. We've built space that lets you run a serious practice without paying for a full-time office.",
    audience: [
      "Mobile notaries with a base office",
      "Signing agents (loan signings)",
      "Apostille & document services",
      "Bilingual notary practices",
    ],
    benefits: [
      { title: "Walk-in friendly reception", body: "Front desk welcomes walk-ins for quick notarisations and witness coordination." },
      { title: "Private signing rooms", body: "Quiet, closed rooms for sensitive document signings — never in a public lobby." },
      { title: "Hourly room rentals", body: "Don't need a full-time office? Book a signing room for the hour you actually need it." },
      { title: "Witness coordination", body: "Reception can help arrange a second witness on short notice for tenant practices that need one." },
      { title: "Co-located professional network", body: "Title companies, attorneys and tax preparers in the same building — natural referral flow." },
      { title: "Visible lobby directory", body: "Your service listed in the directory so building tenants find you first." },
    ],
    recommendedSlugs: ["private-offices", "executive-suites", "office-suites"],
    heroImage: IMG.lobby,
    defaultOfficeType: "Private Offices",
  },
  {
    slug: "office-space-for-court-reporters-weston-fl",
    category: "Other Professional Services",
    metaTitle: "Office Space for Court Reporters & Litigation Support in Weston, FL",
    metaDescription:
      "Office space for court reporters and litigation support firms in Weston, FL — deposition-ready boardrooms, secure record storage and bookable rooms by the hour.",
    h1: "Office space for court reporters & litigation support.",
    intro:
      "Depositions need quiet, properly lit conference rooms with reliable power and decent acoustics. We've got them — by the hour, the day, or full-time.",
    audience: [
      "Court reporting agencies",
      "Independent court reporters",
      "Videographers for depositions",
      "Legal exhibit prep & graphics firms",
    ],
    benefits: [
      { title: "Deposition-ready boardrooms", body: "Long tables, good acoustics, dimmable lighting — properly suited for full-day depositions." },
      { title: "Hourly & daily rentals", body: "Book rooms by the hour or the day — perfect for agencies that schedule depositions citywide." },
      { title: "Reliable power & internet", body: "Stable power and gigabit internet for streaming reporters, AV gear, and remote witnesses." },
      { title: "Secure record storage", body: "Locked storage for exhibits, sealed transcripts, and litigation support files." },
      { title: "Near the legal corridor", body: "Surrounded by attorneys and mediators — walk-distance referral business." },
      { title: "Witness-friendly reception", body: "Front desk used to checking in opposing counsel, witnesses, videographers and translators." },
    ],
    recommendedSlugs: ["office-suites", "executive-suites", "private-offices"],
    heroImage: IMG.meeting,
    defaultOfficeType: "Office Suites",
  },
  {
    slug: "office-space-for-translation-services-weston-fl",
    category: "Other Professional Services",
    metaTitle: "Office Space for Translation Services in Weston, FL",
    metaDescription:
      "Translation and interpretation services office space in Weston, FL — bilingual reception, walk-in friendly suites and a central location for South Florida's multilingual market.",
    h1: "Office space for translation & interpretation services.",
    intro:
      "South Florida's most linguistically rich market — and a building used to handling walk-ins who need a document translated, certified or notarised today. Bilingual reception, on-site notary, fast turnaround.",
    audience: [
      "Certified translators",
      "Court & medical interpreters",
      "Apostille & legalisation services",
      "Localisation & language services agencies",
    ],
    benefits: [
      { title: "Bilingual reception", body: "Front desk staff comfortable in Spanish, Portuguese and English to greet your clients." },
      { title: "Walk-in friendly suites", body: "Clients can drop in for document translation and certification without an appointment." },
      { title: "On-site notary access", body: "Pair translation with notarisation in a single visit — no second-trip friction." },
      { title: "Affordable starting rates", body: "Private offices from $895/month — a real address without a coworking compromise." },
      { title: "Visible lobby directory", body: "Listed in the building directory so prospects walking in find you instantly." },
      { title: "Direct I-75 access", body: "Clients across South Florida reach you easily — central to Hollywood, Hialeah and Doral." },
    ],
    recommendedSlugs: ["private-offices", "executive-suites", "office-suites"],
    heroImage: IMG.lobby,
    defaultOfficeType: "Private Offices",
  },
  {
    slug: "office-space-for-private-investigators-weston-fl",
    category: "Other Professional Services",
    metaTitle: "Office Space for Private Investigators in Weston, FL",
    metaDescription:
      "Private investigator office space in Weston, FL — discreet, secure suites, confidential meeting rooms, and a low-profile building for an industry that values it.",
    h1: "Office space for private investigators.",
    intro:
      "PIs work for attorneys, insurance companies, and individuals who want privacy. Our building offers a low-key professional presence — no bright signage, no public-facing brand demand — and the secure environment your case files require.",
    audience: [
      "Licensed private investigators",
      "Insurance fraud investigators",
      "Process servers with an office base",
      "Background-check & due-diligence firms",
    ],
    benefits: [
      { title: "Low-profile professional presence", body: "Listed quietly in the building directory — no bright street-facing brand required." },
      { title: "Locked, monitored offices", body: "Every suite individually lockable; alarmed building with cameras on every common area." },
      { title: "Discreet client meetings", body: "Private conference rooms away from foot traffic — clients arrive and leave unnoticed." },
      { title: "Secure document storage", body: "Locked file rooms and certified on-site shredding for case files and evidence logs." },
      { title: "Direct freeway access", body: "Easy reach to attorneys' offices across Broward and Miami-Dade for case-review meetings." },
      { title: "Attorney-adjacent referrals", body: "Surrounded by law firms in the building — natural referral pipeline." },
    ],
    recommendedSlugs: ["private-offices", "executive-suites", "office-suites"],
    heroImage: IMG.exterior,
    defaultOfficeType: "Private Offices",
  },
];

export const SEO_LANDINGS_BY_CATEGORY = SEO_LANDINGS.reduce((acc, l) => {
  (acc[l.category] = acc[l.category] || []).push(l);
  return acc;
}, {});

export default SEO_LANDINGS;
